import { Injectable } from '@angular/core';
import { MenuItems } from 'src/app/auth/menu-items';

export enum Tier {
  STARTER = 'starter',
  PRO = 'pro',
  ENTERPRISE = 'enterprise',
  UNKNOWN = 'unknown',
}

export enum TierDisplayName {
  STARTER = 'Starter',
  PRO = 'Pro',
  ENTERPRISE = 'Enterprise',
  UNKNOWN = 'Unknown',
}

export enum TierIcon {
  STARTER = 'fas fa-star',
  PRO = 'fas fa-certificate',
  ENTERPRISE = 'fas fa-rocket',
  UNKNOWN = 'fas fa-question-circle',
}

export enum TierBadgeClass {
  STARTER = 'tier-starter',
  PRO = 'tier-pro',
  ENTERPRISE = 'tier-enterprise',
  UNKNOWN = 'tier-unknown',
}

export interface TierInfo {
  displayName: string;
  icon: string;
  badgeClass: string;
}

export enum TierLevel {
  STARTER = 1,
  PRO = 2,
  ENTERPRISE = 3,
  UNKNOWN = 0,
}

@Injectable({
  providedIn: 'root',
})
export class TierService {
  constructor() {}

  /**
   * Normalize and parse tier string to enum
   * @param tier The tier string from the organization
   * @returns The normalized Tier enum value
   */
  private parseTier(tier: string | undefined | null): Tier {
    if (!tier) {
      return Tier.UNKNOWN;
    }

    const normalizedTier = tier.toLowerCase();
    switch (normalizedTier) {
      case Tier.STARTER:
        return Tier.STARTER;
      case Tier.PRO:
        return Tier.PRO;
      case Tier.ENTERPRISE:
        return Tier.ENTERPRISE;
      default:
        return Tier.UNKNOWN;
    }
  }

  /**
   * Normalize tier to treat UNKNOWN as STARTER
   * This ensures null/unknown tiers are handled as starter tier by default
   * @param tier The tier enum value
   * @returns The normalized tier with UNKNOWN converted to STARTER
   */
  private normalizeTierAsStarter(tier: Tier): Tier {
    return tier === Tier.UNKNOWN ? Tier.STARTER : tier;
  }

  /**
   * Get the display name for a tier
   * @param tier The tier string from the organization
   * @returns The formatted display name
   */
  getTierDisplayName(tier: string | undefined | null): string {
    const parsedTier = this.parseTier(tier);
    const normalizedTier = this.normalizeTierAsStarter(parsedTier);
    switch (normalizedTier) {
      case Tier.STARTER:
        return TierDisplayName.STARTER;
      case Tier.PRO:
        return TierDisplayName.PRO;
      case Tier.ENTERPRISE:
        return TierDisplayName.ENTERPRISE;
      default:
        return TierDisplayName.UNKNOWN;
    }
  }

  /**
   * Get the Font Awesome icon class for a tier
   * @param tier The tier string from the organization
   * @returns The Font Awesome icon class
   */
  getTierIcon(tier: string | undefined | null): string {
    const parsedTier = this.parseTier(tier);
    const normalizedTier = this.normalizeTierAsStarter(parsedTier);
    switch (normalizedTier) {
      case Tier.STARTER:
        return TierIcon.STARTER;
      case Tier.PRO:
        return TierIcon.PRO;
      case Tier.ENTERPRISE:
        return TierIcon.ENTERPRISE;
      default:
        return TierIcon.UNKNOWN;
    }
  }

  /**
   * Get the CSS badge class for a tier
   * @param tier The tier string from the organization
   * @returns The CSS class name
   */
  getTierBadgeClass(tier: string | undefined | null): string {
    const parsedTier = this.parseTier(tier);
    const normalizedTier = this.normalizeTierAsStarter(parsedTier);
    switch (normalizedTier) {
      case Tier.STARTER:
        return TierBadgeClass.STARTER;
      case Tier.PRO:
        return TierBadgeClass.PRO;
      case Tier.ENTERPRISE:
        return TierBadgeClass.ENTERPRISE;
      default:
        return TierBadgeClass.UNKNOWN;
    }
  }

  /**
   * Get all tier information at once
   * @param tier The tier string from the organization
   * @returns Object containing all tier information
   */
  getTierInfo(tier: string | undefined | null): TierInfo {
    return {
      displayName: this.getTierDisplayName(tier),
      icon: this.getTierIcon(tier),
      badgeClass: this.getTierBadgeClass(tier),
    };
  }

  /**
   * Get the numeric tier level for comparison purposes
   * @param tier The tier string from the organization
   * @returns The tier level number
   */
  getTierLevel(tier: string | undefined | null): TierLevel {
    const parsedTier = this.parseTier(tier);
    const normalizedTier = this.normalizeTierAsStarter(parsedTier);
    switch (normalizedTier) {
      case Tier.STARTER:
        return TierLevel.STARTER;
      case Tier.PRO:
        return TierLevel.PRO;
      case Tier.ENTERPRISE:
        return TierLevel.ENTERPRISE;
      default:
        return TierLevel.UNKNOWN;
    }
  }

  /**
   * Check if a tier meets the minimum required tier level
   * @param tier The tier string from the organization
   * @param minimumTier The minimum required tier
   * @returns True if the tier meets or exceeds the minimum requirement
   */
  meetsMinimumTier(
    tier: string | undefined | null,
    minimumTier: Tier
  ): boolean {
    const currentLevel = this.getTierLevel(tier);
    const requiredLevel = this.getTierLevel(minimumTier);
    return currentLevel >= requiredLevel;
  }

  /**
   * Check if a specific menu item should be visible for the given tier
   * @param tier The tier string from the organization
   * @param menuItemText The menu item text (e.g., "General", "Actions", etc.)
   * @returns True if the menu item should be visible
   */
  isMenuItemVisible(
    tier: string | undefined | null,
    menuItemText: string
  ): boolean {
    const parsedTier = this.parseTier(tier);

    // Menu items hidden for Starter tier (using actual menu item text values)
    const starterHiddenItems = [
      MenuItems.BotGeneral.text,
      MenuItems.BotActions.text,
      MenuItems.BotRoles.text,
      MenuItems.BotForms.text,
      MenuItems.BotAudience.text,
      MenuItems.BotResources.text,
      MenuItems.BotDialogMessages.text,
      MenuItems.BotTags.text,
      MenuItems.BotDialogs.text,
    ];

    if (
      (parsedTier === Tier.STARTER || parsedTier === Tier.UNKNOWN) &&
      starterHiddenItems.includes(menuItemText)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Check if the tier is Starter
   * @param tier The tier string from the organization
   * @returns True if the tier is Starter
   */
  isStarter(tier: string | undefined | null): boolean {
    return (
      this.parseTier(tier) === Tier.STARTER ||
      this.parseTier(tier) === Tier.UNKNOWN
    );
  }

  /**
   * Check if the tier is Pro
   * @param tier The tier string from the organization
   * @returns True if the tier is Pro
   */
  isPro(tier: string | undefined | null): boolean {
    return this.parseTier(tier) === Tier.PRO;
  }

  /**
   * Check if the tier is Enterprise
   * @param tier The tier string from the organization
   * @returns True if the tier is Enterprise
   */
  isEnterprise(tier: string | undefined | null): boolean {
    return this.parseTier(tier) === Tier.ENTERPRISE;
  }
}
